import Cookies from "js-cookie";

import { Order, OrderList, OrderPayment, OrderStatus } from "../types/order";
import { PosCustomerInfo } from "../types/user";
import { getPosCartId } from "./cart.core";
import { get, post } from "./fetch.core";
import { Translation } from "./translation.core";
import { getSessionId } from "./checkout.core";
import { RecycleList } from "../types/recycle";
import { CheckoutContext } from "../states/online-checkout.state";
import { PickupAddr, SfLocation, ShipAddr } from "../types/shipAddr";
import { getLoginToken, getRetailShopId, getShopId } from "./retailShop.core";

export const OrderStatusArr = ["pending", "partailPayment", , "completed", "cancelled", "returned"];

interface LoadOrderParams {
  paging: {
    size: number;
    page: number;
  };
  paymentPortion: string;
  pickupStatus: string;
  orderStatuses: OrderStatus[];
  paymentStatus: string;
  search: string;
  productSearch: string;
  date: {
    from: string;
    to: string;
  };
}

async function createOnlineOrder(checkoutContext: CheckoutContext) {
  const cartId = await getPosCartId();
  const orderData = getOrderObject(checkoutContext);

  const res = await post(
    "/public/order/createOrder",
    {
      posOnlineOrder: true,
      retailShopId: getRetailShopId(),
      ...orderData,
      cartId,
    },
    { useUserId: checkoutContext.customer!.id }
  );

  return res.data;
}

const getOrderObject = (context: CheckoutContext) => {
  const sessionId = Cookies.get("sessionId");

  return {
    addrType: context.addrType,
    countryId: context.shipCountry.id,
    id: context.orderId,
    orderNote: context.orderNote,
    pickupAddrId: context.addrType === "pickupLocation" ? (context.addr as PickupAddr).id : null,
    sessionId,
    sfLocationCode: context.addrType === "sfLocation" ? (context.addr as SfLocation).code : null,
    sfRecipientName: context.addrType === "sfLocation" ? (context.addr as SfLocation).recipientName : null,
    sfRecipientTel: context.addrType === "sfLocation" ? (context.addr as SfLocation).tel : null,
    sfRecipientTelCountryCode: context.addrType === "sfLocation" ? (context.addr as SfLocation).telCountryCode : null,
    shipAddrId: context.addrType === "regular" ? (context.addr as ShipAddr).id : null,
    shipId: context.shipId,
    shipName: context.shipName,
    shipType: context.shipType,
  };
};

function getOrderStatusLocale(orderStatus: any, { t, msg }: Translation) {
  switch (orderStatus) {
    case "pending":
      return t(msg.order.pending);
    case "processing":
      return t(msg.order.pending);
    case "completed":
      return t(msg.order.completed);
    case "cancelled":
      return t(msg.order.cancelled);
    case "returned":
      return t(msg.order.returned);
    case "partailPayment":
      return t(msg.order.partailPayment);
    default:
      return null;
  }
}

export async function loadOrders(params: Partial<LoadOrderParams>) {
  if (params.orderStatuses) {
    if (params.orderStatuses.length === 0) delete params.orderStatuses;
  }

  // looks like this is a react form hook bug
  if ((params.orderStatuses as any) === false) {
    delete params.orderStatuses;
  }

  if (params.search === "") {
    delete params.search;
  }

  if (params.productSearch === "") {
    delete params.productSearch;
  }

  if (params.date) {
    if (!params.date.from || !params.date.to) delete params.date;
  }

  if (params.pickupStatus === "NA") {
    delete params.pickupStatus;
  }

  const res = await post<OrderList>("/pos/order/list", params);
  return res.data;
}

export async function loadRecycleProducts(params: Partial<LoadOrderParams>) {
  if (params.search === "") {
    delete params.search;
  }

  if (params.date) {
    if (!params.date.from || !params.date.to) delete params.date;
  }

  const res = await post<RecycleList>("/pos/order/listRecycleProducts", params);
  return res.data;
}

export async function getPosSingleOrder(id: string): Promise<Order> {
  const res = await get<Order>(`/pos/order/single/${id}`);
  return res.data;
}

interface ICreatePosOrder {
  userId: string;
  orderNote?: string;
  customerInfo?: PosCustomerInfo;
  gender?: string;
  age?: string;
}
[];

export async function newPosOrder({ userId, customerInfo, gender, age }: ICreatePosOrder) {
  const cartId = await getPosCartId();
  const sessionId = getSessionId();

  const payload = {
    cartId,
    customerInfo,
    sessionId,
    userId,
    gender,
    age,
  };

  const res = await post<Order>("/pos/order/createOrder", {
    ...payload,
  });

  Cookies.remove("sessionId");
  return res.data;
}

interface UpdatePosOrder {
  orderId: string;
  payment?: OrderPayment;
  orderNote?: string;
  customerInfo?: any;
  gender?: string;
  age?: string;
}

export async function updateOrder({ orderId, payment, customerInfo, orderNote, gender, age }: UpdatePosOrder) {
  const res = await post<{ message: string; order: Order }>("/pos/order/updateOrder", {
    customerInfo,
    orderId,
    orderNote,
    payment,
    gender,
    age,
  });
  return res.data.order;
}

export async function updateOrderNote({ orderId, orderNote }: { orderId: string; orderNote: string }) {
  const res = await post<{ message: string; order: Order }>("/pos/order/updateOrder", {
    orderId,
    orderNote,
  });
  return res.data.order;
}

export async function updateOrderEyeInfo({ orderId, eyeInfo }: { orderId: string; eyeInfo: any }) {
  const res = await post<{ message: string; order: Order }>("/pos/order/updateOrder", {
    customerInfo: {
      eyeInfo: eyeInfo,
    },
    orderId,
  });
  return res.data.order;
}

export async function makeZeroDeposit({ orderId, customerInfo }: UpdatePosOrder) {
  const res = await post<{ message: string; order: Order }>("/pos/order/makeZeroDeposit", {
    customerInfo,
    orderId,
  });
  return res.data;
}

export async function sendEmail({ orderId }: { orderId: string }) {
  const res = await post<{ message: string }>("/pos/order/sendOrderEmail", {
    orderId,
  });
  return res.data;
}

export async function pickup({
  orderId,
  pickupDate,
  pickupStatus,
}: {
  orderId: string;
  pickupDate: string;
  pickupStatus?: string;
}) {
  const res = await post<{ message: string }>("/pos/order/pickup", {
    orderId,
    pickupDate,
    pickupStatus,
  });

  return res.data;
}

export async function orderStats(params?: any) {
  if (params.date) {
    if (!params.date.from || !params.date.to) delete params.date;
  }

  const res = await post<any>("/pos/order/stats", params);
  return res.data;
}

async function printOrderReceipt(orderId: string) {
  const res = await post<{ message: string }>(`/pos/order/printReceipt?orderId=${orderId}`, {});
  return res.data;
}

async function downloadOrderInvoicePdf(orderId: string): Promise<Blob> {
  const url = `/pos/order/printInvoicePdf?orderId=${orderId}`;

  const headers: { [key: string]: string } = {
    locale: "en",
    "retailshop-id": getRetailShopId()!,
    "shop-id": getShopId()!,
    "x-access-token": getLoginToken()!,
  };

  const response = await fetch(process.env.NEXT_PUBLIC_URL_ROOT + url, {
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
    throw new Error(`Failed to download PDF: ${errorData.message || response.statusText}`);
  }

  return await response.blob();
}

async function voidOrder({ orderId }: { orderId: string }) {
  const res = await post<{ message: string }>("/pos/order/void", { orderId });
  return res.data;
}

async function refundOrder({ orderId }: { orderId: string }) {
  const res = await post<{ message: string }>("/pos/order/refund", { orderId });
  return res.data;
}

async function recycleProduct(data: { orderId: string; productId: string; quantity: number }) {
  const res = await post<{ message: string }>("/pos/order/receiveRecycle", data);
  return res.data;
}

async function getCombineOrder({ sessionId, userId }: { sessionId: string; userId: string }) {
  const res = await get<{ message: string; orders: Order[] }>(`/public/order/getCombineOrder?sessionId=${sessionId}`, {
    useUserId: userId,
  });
  return res.data;
}
interface CombineOrderOptions {
  orderId: string;
  cartId: string;
  sessionId: string;
  userId: string;
}
async function combineOrder({ orderId, cartId, sessionId, userId }: CombineOrderOptions) {
  const res = await post<{ message: string }>(
    "/public/order/combineOrder",
    { id: orderId, cartId, sessionId },
    { useUserId: userId }
  );
  return res.data;
}

export async function updatePaymentMethod({
  orderId,
  paymentId,
  paymentName,
}: {
  orderId: string;
  paymentId: string;
  paymentName: string;
}): Promise<Order> {
  const res = await post<{ message: string; order: Order }>("/pos/order/updatePaymentMethod", {
    orderId,
    paymentId,
    paymentName,
  });
  return res.data.order;
}

const orderCore = {
  createOnlineOrder,
  downloadOrderInvoicePdf,
  getOrderStatusLocale,
  loadOrders,
  makeZeroDeposit,
  orderStats,
  pickup,
  printOrderReceipt,
  recycleProduct,
  refundOrder,
  sendEmail,
  updateOrder,
  updateOrderEyeInfo,
  updateOrderNote,
  deleteOrderNote,
  updatePaymentMethod,
  voidOrder,
  getCombineOrder,
  combineOrder,
};

async function deleteOrderNote({ orderId, noteId }: { orderId: string; noteId: string }) {
  const res = await post<{ message: string }>("/pos/order/deleteNote", {
    orderId,
    noteId,
  });
  return res.data;
}

orderCore.deleteOrderNote = deleteOrderNote;

export default orderCore;
